
import { useState, useEffect, useCallback } from 'react';
import localVersion from '../version.json';
import { logger } from '@deskbay/shared';

interface VersionData {
    version: string;
    buildTime: number;
}

export const useVersionCheck = (checkIntervalMs = 60000) => {
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [serverVersion, setServerVersion] = useState<VersionData | null>(null);

    const checkForUpdate = async () => {
        try {
            // Append timestamp to bypass caches (especially Safari)
            const response = await fetch(`/version.json?t=${new Date().getTime()}`, {
                cache: 'no-store',
            });

            if (!response.ok) return;

            const data: VersionData = await response.json();

            // Compare build times
            // If server build time is significantly newer than local build time
            if (data.buildTime > localVersion.buildTime) {
                setServerVersion(data);
                setIsUpdateAvailable(true);
                logger.api.info('Update available', 'useVersionCheck', { version: data.version });
            }
        } catch (error) {
            logger.api.error('Error checking for updates', error, 'useVersionCheck');
        }
    };

    useEffect(() => {
        // Check on mount
        checkForUpdate();

        // Check on window focus (user comes back to tab)
        const onFocus = () => checkForUpdate();
        window.addEventListener('focus', onFocus);

        // Check on visibility change (mobile app resume)
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkForUpdate();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        // Periodic check
        const interval = setInterval(checkForUpdate, checkIntervalMs);

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            clearInterval(interval);
        };
    }, [checkIntervalMs]);

    const reloadApp = useCallback(() => {
        // Force reload ignoring cache
        window.location.reload();
    }, []);

    return { isUpdateAvailable, reloadApp, currentVersion: localVersion, serverVersion };
};
